import { PaymentElement } from '@stripe/react-stripe-js';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

import { getVatDetails } from '../utils/vat';

function ShoppingCart({ cartItems, customer, onRemoveFromCart, onPlaceOrder, isPlacingOrder, t, language, onPayPalSuccess, purchaseOrder, setPurchaseOrder, onTotalChange }) {
  const isPostpaid = customer?.isPostpaid || false;

  const subtotal = cartItems.reduce((total, item) => {
    return total + (item.price || 0) * item.quantity;
  }, 0);
  const { vatRate, vatPercent } = getVatDetails(customer);
  const vatPercentLabel = `VAT ${vatPercent}%`;
  const translatedVatLabel = t.vat ? `${t.vat} ${vatPercent}%` : vatPercentLabel;

  const [paymentMethod, setPaymentMethod] = useState('card');
  // const [setErrorMessage] = useState(null);
  const [isTcChecked, setIsTcChecked] = useState(false);

  const paypalFee = paymentMethod === 'paypal' ? subtotal * 0.03 : 0;
  const vatAmount = (subtotal + paypalFee) * vatRate;
  const finalTotalPrice = subtotal + paypalFee + vatAmount;

  useEffect(() => {
    if (onTotalChange) {
      onTotalChange(finalTotalPrice);
    }
  }, [finalTotalPrice, onTotalChange]);

  return (
    <div className={`sticky top-8 transition-all duration-300 ${isPlacingOrder ? '-mt-8 animated-gradient-wrapper shadow-lg' : 'rounded-lg border border-gray-100 shadow-sm'}`}>
      <div className={`p-6 bg-white relative h-full ${isPlacingOrder ? 'animated-gradient-inner' : 'rounded-lg overflow-hidden'}`}>
        {/* Loading Overlay */}
        {isPlacingOrder && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] z-50 rounded-lg"></div>
        )}
        <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
          <svg className="w-8 h-8 mr-3" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
            <g opacity="0.5">
              <path d="M156.25 468.751C173.509 468.751 187.5 454.76 187.5 437.501C187.5 420.242 173.509 406.251 156.25 406.251C138.991 406.251 125 420.242 125 437.501C125 454.76 138.991 468.751 156.25 468.751Z" fill="#161616" />
              <path d="M375 468.751C392.259 468.751 406.25 454.76 406.25 437.501C406.25 420.242 392.259 406.251 375 406.251C357.741 406.251 343.75 420.242 343.75 437.501C343.75 454.76 357.741 468.751 375 468.751Z" fill="#161616" />
              <path d="M437.5 109.376H90.9375L78.125 43.7506C77.3945 40.1682 75.4309 36.9554 72.5759 34.6714C69.7209 32.3874 66.1554 31.1769 62.5 31.2506H0V62.5006H49.6875L109.375 362.501C110.105 366.083 112.069 369.296 114.924 371.58C117.779 373.864 121.345 375.074 125 375.001H406.25V343.751H137.812L125 281.251H406.25C409.862 281.339 413.393 280.173 416.242 277.951C419.09 275.729 421.081 272.588 421.875 269.063L453.125 128.438C453.649 126.12 453.636 123.713 453.089 121.4C452.542 119.087 451.475 116.929 449.968 115.091C448.461 113.253 446.555 111.782 444.395 110.792C442.234 109.802 439.876 109.317 437.5 109.376ZM393.75 250.001H119.062L97.1875 140.626H417.969L393.75 250.001Z" fill="#161616" />
            </g>
          </svg>
          {t.shopping_cart}
        </h2>
        {cartItems.length === 0 ? (
          <div className="text-center py-10">
            <svg className="w-16 h-16 mx-auto mb-4" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g opacity="0.5">
                <path d="M156.25 468.751C173.509 468.751 187.5 454.76 187.5 437.501C187.5 420.242 173.509 406.251 156.25 406.251C138.991 406.251 125 420.242 125 437.501C125 454.76 138.991 468.751 156.25 468.751Z" fill="#161616" />
                <path d="M375 468.751C392.259 468.751 406.25 454.76 406.25 437.501C406.25 420.242 392.259 406.251 375 406.251C357.741 406.251 343.75 420.242 343.75 437.501C343.75 454.76 357.741 468.751 375 468.751Z" fill="#161616" />
                <path d="M437.5 109.376H90.9375L78.125 43.7506C77.3945 40.1682 75.4309 36.9554 72.5759 34.6714C69.7209 32.3874 66.1554 31.1769 62.5 31.2506H0V62.5006H49.6875L109.375 362.501C110.105 366.083 112.069 369.296 114.924 371.58C117.779 373.864 121.345 375.074 125 375.001H406.25V343.751H137.812L125 281.251H406.25C409.862 281.339 413.393 280.173 416.242 277.951C419.09 275.729 421.081 272.588 421.875 269.063L453.125 128.438C453.649 126.12 453.636 123.713 453.089 121.4C452.542 119.087 451.475 116.929 449.968 115.091C448.461 113.253 446.555 111.782 444.395 110.792C442.234 109.802 439.876 109.317 437.5 109.376ZM393.75 250.001H119.062L97.1875 140.626H417.969L393.75 250.001Z" fill="#161616" />
              </g>
            </svg>
            <p className="text-gray-500">{t.empty_cart}</p>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto max-h-96 pr-2">
              <ul className="space-y-4">
                {cartItems.map(item => (
                  <li key={item.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex-grow">
                      <p className="font-semibold text-gray-800 flex items-center flex-wrap">
                        <span>{item.name}</span>
                        {item.isRenewal && (
                          <span className="ml-2 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full inline-block mt-1 sm:mt-0">
                            {t.renewal || 'Renewal'}
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-600">{t.quantity}: {item.quantity}</p>
                      {item.yearlyPrice && (
                        <p className="text-sm text-gray-600">{t.yearly_price}: €{Number(item.yearlyPrice).toFixed(2)}</p>
                      )}
                      {item.diffDays && (
                        <p className="text-sm text-gray-600 border-b">{t.remain_days}: {item.diffDays}</p>
                      )}
                      {item.price && (
                        <p className="text-sm text-gray-500">
                          €{(item.price * item.quantity).toFixed(2)}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => onRemoveFromCart(item.id)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                      title={t.remove}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">{t.subtotal || 'Subtotal'}</span>
                <span className="font-medium text-gray-800">€{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center mb-4">
                <span className="text-gray-600">{translatedVatLabel}</span>
                <span className="font-medium text-gray-800">€{vatAmount.toFixed(2)}</span>
              </div>
              {!isPostpaid && paymentMethod === 'paypal' && (
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">PayPal Fee (3%)</span>
                  <span className="font-medium text-gray-800">€{paypalFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center border-t border-gray-100 pt-3">
                <span className="text-lg font-semibold text-gray-700">
                  {t.total || 'Total'}
                </span>
                <span className="text-2xl font-bold text-gray-900">
                  €{finalTotalPrice.toFixed(2)}
                </span>
              </div>
            </div>

            {cartItems.length > 0 && (
              <>
                <div className="mb-6">
                  <label htmlFor="purchaseOrder" className="block text-gray-700 text-sm font-bold mb-2">
                    {t.purchase_order || "Purchase Order"}
                  </label>
                  <input
                    type="text"
                    id="purchaseOrder"
                    value={purchaseOrder}
                    onChange={(e) => setPurchaseOrder(e.target.value)}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    placeholder="PO-12345"
                  />
                </div>

                {/* {!isPostpaid && (
                <div className="flex space-x-4 mb-6">
                  <div className="w-full py-2 px-4 rounded border bg-blue-50 border-blue-500 text-blue-700 font-bold text-center">
                    Payment Method
                  </div>
                </div>
              )} */}

                <div className="mb-6 flex flex-row items-center border border-gray-200 bg-gray-50 p-4 rounded-lg">
                  <input
                    type="checkbox"
                    id="tc-checkbox"
                    checked={isTcChecked}
                    onChange={(e) => setIsTcChecked(e.target.checked)}
                    className="w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <label htmlFor="tc-checkbox" className="ml-3 text-sm text-gray-700 font-medium cursor-pointer">
                    {t.tc_agree || "I agree to the"}{" "}
                    <a
                      href={language === 'nl' ? '/TermsConditionsNL.html' : '/TermsConditionsEN.html'}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {t.tc_terms || "Terms and Conditions"}
                    </a>
                  </label>
                </div>
              </>
            )}

            {isPostpaid ? (
              <button
                type="submit"
                onClick={(e) => {
                  if (!isTcChecked) {
                    e.preventDefault();
                    toast.error(t.tc_error || "Please agree to the Terms and Conditions to proceed.");
                  }
                }}
                disabled={isPlacingOrder}
                className={`w-full mt-6 font-bold py-3 px-4 rounded-lg text-lg transition-all ${isPlacingOrder
                  ? 'bg-gray-400 text-gray-800 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600 text-white transform hover:scale-105'
                  }`}
              >
                {isPlacingOrder ? t.loading || 'Plaatsen...' : t.place_order}
              </button>
            ) : (
              <>
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">
                    {t.payment_details || 'Betalingsgegevens'}
                  </h3>
                  <div
                    style={{
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '5px',
                    }}
                  >
                    <PaymentElement
                      onChange={(event) => {
                        if (event.value?.type === 'paypal') {
                          setPaymentMethod('paypal');
                        } else {
                          setPaymentMethod(event.value?.type || 'card');
                        }
                      }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  onClick={(e) => {
                    if (!isTcChecked) {
                      e.preventDefault();
                      toast.error(t.tc_error || "Please agree to the Terms and Conditions to proceed.");
                    }
                  }}
                  disabled={isPlacingOrder}
                  className={`w-full mt-6 font-bold py-3 px-4 rounded-lg text-lg transition-all ${isPlacingOrder
                    ? 'bg-gray-400 text-gray-800 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600 text-white transform hover:scale-105'
                    }`}
                >
                  {isPlacingOrder ? t.loading || 'Plaatsen...' : t.place_order}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ShoppingCart;
